import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { Link } from '../../../types';
import { useState, useEffect } from 'react';

const COLLECTION_NAME = 'links';

const toLink = (d: any): Link => {
  const data = d.data();
  return {
    id: d.id,
    platform: data.platform,
    caseNumber: String(data.caseNumber ?? ''),
    url: data.url || undefined,
    donorName: data.donorName || undefined,
    linkTitle: data.linkTitle || undefined,
    treatmentCategoryId: data.treatmentCategoryId || undefined,
    actualReceived: data.actualReceived || 0,
    addedAt: data.addedAt?.toDate() || new Date(),
    addedBy: data.addedBy || undefined,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
};

export const subscribeLinks = (callback: (links: Link[]) => void) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(toLink));
  });
};

export const addLink = async (
  entry: Omit<Link, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const data: any = {
    ...entry,
    addedAt: entry.addedAt instanceof Date ? Timestamp.fromDate(entry.addedAt) : Timestamp.now(),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  // Firestore rejects undefined
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  const docRef = await addDoc(collection(db, COLLECTION_NAME), data);
  return docRef.id;
};

export const updateLink = async (
  id: string,
  updates: Partial<Omit<Link, 'id' | 'createdAt'>>
): Promise<void> => {
  const data: any = { ...updates, updatedAt: Timestamp.now() };
  if (data.addedAt instanceof Date) data.addedAt = Timestamp.fromDate(data.addedAt);
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  await updateDoc(doc(db, COLLECTION_NAME, id), data);
};

export const deleteLink = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
};

export const useLinks = () => {
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeLinks((items) => {
      setLinks(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return { links, loading };
};
